import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Pagination,
  Grid,
  CircularProgress,
  Switch,
  FormControlLabel,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { hasPermission } from '../../../../utils/permissionUtils';
import { formatEnumLabel } from '../../../../constants/hrm';
import {
  usePositions,
  useCreatePosition,
  useUpdatePosition,
  useDeletePosition,
  useDepartments,
} from '../../../../hooks/useHRM';

const initialFormData = {
  title: '',
  description: '',
  department_id: '',
  grade_level: '',
  min_salary: '',
  max_salary: '',
  is_active: true,
};

const PositionList = () => {
  const { chatbotId } = useParams();

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPosition, setEditingPosition] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [formErrors, setFormErrors] = useState({});

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [positionToDelete, setPositionToDelete] = useState(null);

  const [canView, setCanView] = useState(false);
  const [canModify, setCanModify] = useState(false);

  // React Query hooks
  const filters = {
    page,
    per_page: 20,
    ...(search && { search }),
    ...(filterDepartment && { department_id: filterDepartment }),
  };

  const { data: positionData, isLoading: loading } = usePositions(chatbotId, filters);
  const { data: departmentData } = useDepartments(chatbotId, { per_page: 1000 });

  const positions = positionData?.data || [];
  const pagination = positionData?.pagination || { total: 0, pages: 1, per_page: 20 };
  const departments = departmentData?.data || [];

  const createPosition = useCreatePosition(chatbotId);
  const updatePosition = useUpdatePosition(chatbotId);
  const deletePosition = useDeletePosition(chatbotId);

  const saving = createPosition.isPending || updatePosition.isPending;
  const deleting = deletePosition.isPending;

  useEffect(() => {
    const checkPermissions = async () => {
      const view = await hasPermission('hrm', 'view');
      const modify = await hasPermission('hrm', 'add|edit');
      setCanView(view);
      setCanModify(modify);
    };
    checkPermissions();
  }, []);

  const handleOpenCreate = () => {
    setEditingPosition(null);
    setFormData(initialFormData);
    setFormErrors({});
    setDialogOpen(true);
  };

  const handleOpenEdit = (position) => {
    setEditingPosition(position);
    setFormData({
      title: position.title || '',
      description: position.description || '',
      department_id: position.department_id || '',
      grade_level: position.grade_level || '',
      min_salary: position.min_salary ?? '',
      max_salary: position.max_salary ?? '',
      is_active: position.is_active !== undefined ? position.is_active : true,
    });
    setFormErrors({});
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingPosition(null);
    setFormData(initialFormData);
    setFormErrors({});
  };

  const handleFormChange = (field) => (e) => {
    const value = field === 'is_active' ? e.target.checked : e.target.value;
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.title.trim()) errors.title = 'Position title is required';
    if (formData.min_salary && formData.max_salary) {
      if (Number(formData.min_salary) > Number(formData.max_salary)) {
        errors.min_salary = 'Min salary cannot exceed max salary';
      }
    }
    if (formData.min_salary && Number(formData.min_salary) < 0) {
      errors.min_salary = 'Salary cannot be negative';
    }
    if (formData.max_salary && Number(formData.max_salary) < 0) {
      errors.max_salary = 'Salary cannot be negative';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      const payload = { ...formData };

      // Convert empty strings to null for optional fields
      if (!payload.description) payload.description = null;
      if (!payload.department_id) payload.department_id = null;
      if (!payload.grade_level) payload.grade_level = null;
      if (payload.min_salary === '') payload.min_salary = null;
      else payload.min_salary = Number(payload.min_salary);
      if (payload.max_salary === '') payload.max_salary = null;
      else payload.max_salary = Number(payload.max_salary);

      if (editingPosition) {
        await updatePosition.mutateAsync({ id: editingPosition.id, ...payload });
      } else {
        await createPosition.mutateAsync(payload);
      }
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving position:', error);
      if (error.response?.data?.message) {
        setFormErrors({ general: error.response.data.message });
      }
    }
  };

  const handleOpenDelete = (position) => {
    setPositionToDelete(position);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!positionToDelete) return;
    try {
      await deletePosition.mutateAsync(positionToDelete.id);
      setDeleteDialogOpen(false);
      setPositionToDelete(null);
    } catch (error) {
      console.error('Error deleting position:', error);
    }
  };

  const formatSalary = (amount) => {
    if (amount === null || amount === undefined) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const clearFilters = () => {
    setSearch('');
    setFilterDepartment('');
    setPage(1);
  };

  if (!canView) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" color="error">
          You do not have permission to view positions.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
          Positions
        </Typography>
        {canModify && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate} size="small">
            Add Position
          </Button>
        )}
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search positions..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Department</InputLabel>
              <Select
                value={filterDepartment}
                onChange={(e) => { setFilterDepartment(e.target.value); setPage(1); }}
                label="Department"
              >
                <MenuItem value="">All Departments</MenuItem>
                {departments.map((dept) => (
                  <MenuItem key={dept.id} value={dept.id}>
                    {dept.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={2} md={1}>
            <Tooltip title="Clear filters">
              <IconButton onClick={clearFilters}>
                <ClearIcon />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>
      </Paper>

      {/* Position Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
            <Table sx={{ minWidth: 700 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Title</TableCell>
                  <TableCell>Department</TableCell>
                  <TableCell>Grade Level</TableCell>
                  <TableCell>Salary Range</TableCell>
                  <TableCell align="center">Employee Count</TableCell>
                  <TableCell align="center">Status</TableCell>
                  {canModify && <TableCell align="right">Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {positions.length > 0 ? (
                  positions.map((position) => (
                    <TableRow key={position.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {position.title}
                        </Typography>
                        {position.description && (
                          <Typography variant="caption" color="textSecondary" display="block">
                            {position.description.length > 60
                              ? `${position.description.substring(0, 60)}...`
                              : position.description}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{position.department_name || '-'}</TableCell>
                      <TableCell>
                        {position.grade_level ? (
                          <Chip label={position.grade_level} size="small" variant="outlined" />
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>
                        {position.min_salary !== null || position.max_salary !== null ? (
                          <Typography variant="body2">
                            {formatSalary(position.min_salary)} - {formatSalary(position.max_salary)}
                          </Typography>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={position.employee_count ?? 0}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={position.is_active ? 'Active' : 'Inactive'}
                          color={position.is_active ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      {canModify && (
                        <TableCell align="right">
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => handleOpenEdit(position)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleOpenDelete(position)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={canModify ? 7 : 6} align="center" sx={{ py: 4 }}>
                      <Typography color="textSecondary">No positions found</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {pagination.pages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={pagination.pages}
                page={page}
                onChange={(e, value) => setPage(value)}
                color="primary"
              />
            </Box>
          )}
        </>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingPosition ? 'Edit Position' : 'Create Position'}
        </DialogTitle>
        <DialogContent>
          {formErrors.general && (
            <Typography color="error" sx={{ mb: 2 }}>
              {formErrors.general}
            </Typography>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Position Title"
                value={formData.title}
                onChange={handleFormChange('title')}
                error={!!formErrors.title}
                helperText={formErrors.title}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={handleFormChange('description')}
                multiline
                rows={3}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Department</InputLabel>
                <Select
                  value={formData.department_id}
                  onChange={handleFormChange('department_id')}
                  label="Department"
                >
                  <MenuItem value="">None</MenuItem>
                  {departments.map((dept) => (
                    <MenuItem key={dept.id} value={dept.id}>
                      {dept.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Grade Level"
                value={formData.grade_level}
                onChange={handleFormChange('grade_level')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Min Salary"
                type="number"
                value={formData.min_salary}
                onChange={handleFormChange('min_salary')}
                error={!!formErrors.min_salary}
                helperText={formErrors.min_salary}
                InputProps={{ inputProps: { min: 0 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Max Salary"
                type="number"
                value={formData.max_salary}
                onChange={handleFormChange('max_salary')}
                error={!!formErrors.max_salary}
                helperText={formErrors.max_salary}
                InputProps={{ inputProps: { min: 0 } }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={handleFormChange('is_active')}
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={24} /> : editingPosition ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Position</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the position{' '}
            <strong>{positionToDelete?.title}</strong>? This action cannot be undone.
          </Typography>
          {positionToDelete?.employee_count > 0 && (
            <Typography color="error" sx={{ mt: 1 }}>
              Warning: This position has {positionToDelete.employee_count} employee(s) assigned to it.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
            disabled={deleting}
          >
            {deleting ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PositionList;
